// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { NavLink } from 'react-router-dom';
import { AppIcon, FinertyLogo } from '@loa/ui-components';

interface NavItem {
  label: string;
  to: string;
  icon: string;
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: 'dashboard' },
  { label: 'Tasks', to: '/tasks', icon: 'task' },
];

interface SidebarProps {
  providerName?: string;
  logoUrl?: string | null;
}

export function Sidebar({ providerName, logoUrl }: SidebarProps) {
  return (
    <aside
      className="flex flex-col bg-[var(--color-surface)] shrink-0"
      style={{ width: 'var(--sidebar-width)', minHeight: '100vh', boxShadow: 'var(--shadow-panel)' }}
    >
      {/* Logo Area — 80px, centered, bottom border */}
      <div
        className="flex items-center justify-center shrink-0"
        style={{
          height: 'var(--header-height)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={providerName ?? 'Provider'}
            className="h-[40px] w-auto object-contain"
          />
        ) : (
          <span
            className="font-medium"
            style={{ fontSize: 'var(--font-size-body-01)', color: 'var(--color-text-primary)' }}
          >
            {providerName ?? 'Provider Portal'}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-col flex-1 pt-[var(--space-3xl)]" style={{ gap: 'var(--menu-item-gap)' }}>
        {mainNavItems.map(({ label, to, icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => 'no-underline ' + (isActive ? 'sidebar-item-active' : 'sidebar-item')}
          >
            {({ isActive }) => (
              <div
                className={`flex items-center rounded-tr-[var(--radius-md)] rounded-br-[var(--radius-md)] ${
                  isActive ? 'bg-[var(--color-primary-light)]' : 'bg-[var(--color-surface)] hover:bg-gray-50'
                }`}
                style={{ width: 'var(--menu-item-width)', height: 'var(--menu-item-height)' }}
              >
                {/* Active indicator bar */}
                {isActive && (
                  <div
                    className="shrink-0 self-stretch rounded-none"
                    style={{ width: 'var(--menu-active-border-width)', backgroundColor: 'var(--color-primary)' }}
                  />
                )}

                <div
                  className="flex items-center flex-1 gap-3"
                  style={{ paddingLeft: isActive ? '19px' : 'var(--menu-item-padding-x)', paddingRight: 'var(--menu-item-padding-x)' }}
                >
                  <AppIcon
                    name={icon}
                    size={24}
                    color={isActive ? '#2D0A6C' : '#141414'}
                    variant={isActive ? 'Bold' : 'Linear'}
                  />
                  <span
                    className="font-medium"
                    style={{
                      fontSize: 'var(--font-size-body-02)',
                      color: isActive ? 'var(--color-primary-text)' : 'var(--color-text-primary)',
                    }}
                  >
                    {label}
                  </span>
                </div>
              </div>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Powered by Finerty */}
      <div
        className="flex items-center justify-center gap-2 shrink-0"
        style={{ padding: 'var(--space-3xl) 0', borderTop: '1px solid var(--color-border)' }}
      >
        <span style={{ fontSize: 'var(--font-size-body-04)', color: 'var(--color-text-muted)' }}>
          Powered by
        </span>
        <FinertyLogo height={24} />
      </div>
    </aside>
  );
}
